import * as React from "react"
import { useMutation, useQuery } from "react-query"
import dayjs from "dayjs"

import {
  ModalProps,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Flex,
  Avatar,
  Divider,
  Heading,
  Spinner,
  Text,
  Center,
  Button,
  Stack,
} from "@chakra-ui/react"

import { useMe } from "./providers/MeProvider"
import { useOrgs } from "./providers/OrgProvider"
import { fetcher } from "../lib/fetcher"
import { NoData } from "./NoData"
import { UserEntity } from "../lib/types/user"
import { isAdmin, isSupervisor } from "../lib/helpers/role"
import { useToast } from "../lib/hooks/useToast"
import { UserAffiliationControls } from "./UserAffiliationControls"

interface UserModalProps extends Omit<ModalProps, "children"> {
  user: UserEntity
  onRefetch: () => void
}

interface AffiliationsResponse {
  results: Affiliation[]
}

export interface Affiliation {
  uuid: string
  organization_icon: string
  organization_name: string
  role: string
  user: string
}

interface ResetPassword {
  email: string
}

interface UserState {
  userId: string
  action: "disable" | "enable"
}

export const UserModal: React.FC<UserModalProps> = (props) => {
  const me = useMe()
  const toast = useToast()
  const { currentOrgs } = useOrgs()
  const currentOrgsUuids = currentOrgs.map(({ uuid }) => uuid)

  const { data, isLoading, refetch } = useQuery<AffiliationsResponse, string>(
    `affiliations/?user=${
      props.user.uuid
    }&organizations=${currentOrgsUuids.join()}&fields=uuid,organization_icon,organization_name,role,user`,
  )
  const affiliations = data?.results

  const { mutateAsync: setUserState, isLoading: disableLoading } = useMutation<UserState, unknown, UserState>(
    (data) =>
      fetcher(`users/${data.userId}/actions/`, {
        body: JSON.stringify({ type: data.action }),
        method: "post",
      }),
  )

  const { mutateAsync: resetPassword, isLoading: resetLoading } = useMutation<
    ResetPassword,
    unknown,
    ResetPassword
  >((data) => fetcher(`password/forgot/`, { body: JSON.stringify({ email: data.email }), method: "POST" }))

  const handleResetPassword = async () => {
    if (!isSupervisor(me.roles)) return
    try {
      await resetPassword({ email: props.user.email })
    } catch {
    } finally {
      toast({
        title: "Instructions sent!",
        description: "User will find an email from us in your his inbox shortly.",
      })
      props.onRefetch()
    }
  }

  const handleUserState = async () => {
    if (!isAdmin(me.roles)) return
    try {
      const res = await setUserState({
        userId: props.user.uuid,
        action: props.user.state === "disabled" ? "enable" : "disable",
      })
      if (res) {
        props.onRefetch()
        toast({ description: "Successfully changed user state" })
      } else {
        toast({ description: "Error changing user state", status: "error" })
      }
    } catch (error) {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }

  return (
    <Modal {...props} size="xl" isCentered>
      <ModalOverlay zIndex={100}>
        <ModalContent>
          <ModalCloseButton />
          <ModalBody p={6}>
            <Flex align="flex-end" justify="space-between">
              <Flex align="center" opacity={props.user.state === "disabled" ? 0.6 : 1}>
                <Avatar name={props.user.display_name} src={props.user.avatar} size="md" mr={4} />
                <Flex direction="column" align="flex-start" w="100%">
                  <Text fontWeight="bold" color="gray.700">
                    {props.user.display_name}
                  </Text>
                  <Text fontSize="0.8rem" color="gray.500">
                    {props.user.email || "no email"}
                  </Text>
                  <Text fontSize="0.8rem" color="gray.500" w="100%">
                    Joined - {dayjs(props.user.created).format("MMM DD, YYYY")}
                  </Text>
                </Flex>
              </Flex>
              <Stack align="flex-end">
                <Button
                  isLoading={resetLoading}
                  disabled={!isAdmin(me.roles)}
                  fontSize={12}
                  variant="link"
                  onClick={handleResetPassword}
                >
                  Reset password
                </Button>
                <Button
                  isLoading={disableLoading}
                  disabled={!isAdmin(me.roles)}
                  fontSize={12}
                  variant="link"
                  onClick={handleUserState}
                >
                  {props.user.state === "disabled" ? "Enable user" : "Disable user"}
                </Button>
              </Stack>
            </Flex>
            <Divider my={6} />
            <Flex direction="column" w="100%">
              <Heading as="h5" size="sm" fontWeight="normal" color="gray.500" mb={4}>
                Affiliations
              </Heading>
              {isLoading && (
                <Center h="50px">
                  <Spinner />
                </Center>
              )}
              {!isLoading && affiliations && affiliations.length > 0 ? (
                affiliations.map((affiliation, index) => (
                  <UserAffiliationControls
                    key={affiliation.uuid}
                    affiliation={affiliation}
                    affiliationsCount={affiliations.length}
                    index={index}
                    user={props.user}
                    refetch={refetch}
                    onRefetch={props.onRefetch}
                  />
                ))
              ) : (
                <NoData>No affiliations</NoData>
              )}
            </Flex>
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  )
}
